import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Button,
    Chip,
    ListBox,
    Spinner,
    Typography,
} from '@heroui/react';
import { EmptyState } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import type { RosterUser } from '@/api/models';
import { getNodeTestIdProp, getNodeTestProps } from '@/utils';
import { getInitials, getRoleLabelKey, ROLE_META } from '../sellerLoginMeta';
import { SignedOutNotice } from './SignedOutNotice';
import { useRosterFocus } from './useRosterFocus';

type SellerRosterStepProps = {
    branchName: string;
    users: RosterUser[];
    isLoading: boolean;
    isError: boolean;
    /** `pages` key for the roster failure, chosen by the hook. */
    errorKey: string;
    /** `pages` key explaining why the last session ended, or `null`. */
    signOutNoticeKey: string | null;
    /**
     * The seller the roster should focus, resolved by the hook: the
     * previously picked seller if they're still on the roster, otherwise the
     * first one. `null` only when there is nobody to focus.
     */
    focusUserId: string | null;
    onSelect: (user: RosterUser) => void;
    onReEnroll: () => void;
};

/**
 * Step 1 (render-only): the branch roster as a keyboard-operable grid of seller
 * tiles (SEL-35) — a design-system `ListBox` in `layout="grid"`, arrow keys and
 * typeahead native to it, `Enter`/click/tap all firing `onSelect` through
 * `onAction`. It takes focus as soon as it has users to show: the previously
 * picked seller if there is one, the first tile otherwise (`useRosterFocus`).
 * The enrolled branch name anchors the header so the operator can confirm the
 * terminal is bound to the right shop; a discreet re-enroll escape sits in the
 * footer.
 */
const ROSTER_HEADING_ID = 'seller-login-roster-heading';

export const SellerRosterStep: React.FC<SellerRosterStepProps> = ({
    branchName,
    users,
    isLoading,
    isError,
    errorKey,
    signOutNoticeKey,
    focusUserId,
    onSelect,
    onReEnroll,
}) => {
    const { t } = useTranslation('pages');
    const tileRef = useRosterFocus(focusUserId);

    return (
        <div
            className="flex w-full max-w-2xl flex-col gap-6"
            {...getNodeTestProps('seller-login-roster')}
        >
            <div className="flex flex-col items-center gap-1.5 text-center">
                <Chip
                    color="accent"
                    {...getNodeTestProps('seller-login-branch')}
                    variant="soft"
                >
                    <Icon
                        className="size-3.5"
                        icon="solar:shop-2-bold-duotone"
                    />
                    {branchName}
                </Chip>
                <Typography id={ROSTER_HEADING_ID} type="h4" weight="bold">
                    {t('sellerLogin.selectTitle')}
                </Typography>
                <Typography color="muted" type="body-sm">
                    {t('sellerLogin.selectSubtitle')}
                </Typography>
            </div>

            {signOutNoticeKey ? (
                <SignedOutNotice messageKey={signOutNoticeKey} />
            ) : null}

            {isLoading ? (
                <div
                    className="flex min-h-48 items-center justify-center"
                    {...getNodeTestProps('seller-login-roster-loading')}
                >
                    <Spinner aria-label={t('sellerLogin.loadingRoster')} />
                </div>
            ) : isError ? (
                <div
                    className="flex min-h-48 items-center justify-center"
                    {...getNodeTestProps('seller-login-roster-error')}
                >
                    <EmptyState>
                        <EmptyState.Header>
                            <EmptyState.Media variant="icon">
                                <Icon icon="solar:danger-triangle-bold-duotone" />
                            </EmptyState.Media>
                            <EmptyState.Title>{t(errorKey)}</EmptyState.Title>
                        </EmptyState.Header>
                    </EmptyState>
                </div>
            ) : users.length === 0 ? (
                <div
                    className="flex min-h-48 items-center justify-center"
                    {...getNodeTestProps('seller-login-roster-empty')}
                >
                    <EmptyState>
                        <EmptyState.Header>
                            <EmptyState.Media variant="icon">
                                <Icon icon="solar:users-group-rounded-bold-duotone" />
                            </EmptyState.Media>
                            <EmptyState.Title>
                                {t('sellerLogin.rosterEmpty')}
                            </EmptyState.Title>
                        </EmptyState.Header>
                    </EmptyState>
                </div>
            ) : (
                <ListBox
                    aria-labelledby={ROSTER_HEADING_ID}
                    className="grid gap-3"
                    layout="grid"
                    onAction={(key) => {
                        const user = users.find((item) => item.id === key);
                        if (user) {
                            onSelect(user);
                        }
                    }}
                    selectionMode="none"
                    style={{
                        gridTemplateColumns:
                            'repeat(auto-fill,minmax(180px,1fr))',
                    }}
                    {...getNodeTestProps('seller-login-roster-grid')}
                >
                    {users.map((user) => {
                        const meta = ROLE_META[user.role];
                        return (
                            <ListBox.Item
                                className="flex-col items-center gap-3 rounded-2xl border border-border bg-surface p-5 text-center hover:border-accent/40 hover:bg-surface-secondary"
                                id={user.id}
                                key={user.id}
                                ref={tileRef(user.id)}
                                textValue={user.name}
                                {...getNodeTestIdProp(
                                    `seller-login-user-${user.id}`,
                                )}
                            >
                                <Avatar size="lg">
                                    <Avatar.Fallback>
                                        {getInitials(user.name)}
                                    </Avatar.Fallback>
                                </Avatar>
                                <Typography
                                    className="line-clamp-1"
                                    slot="label"
                                    weight="semibold"
                                >
                                    {user.name}
                                </Typography>
                                <Chip
                                    color={meta.color}
                                    size="sm"
                                    variant="soft"
                                >
                                    <Icon
                                        className="size-3.5"
                                        icon={meta.icon}
                                    />
                                    {t(getRoleLabelKey(user.role))}
                                </Chip>
                            </ListBox.Item>
                        );
                    })}
                </ListBox>
            )}

            <div className="flex justify-center">
                <Button
                    {...getNodeTestProps('seller-login-reenroll')}
                    onPress={onReEnroll}
                    size="sm"
                    variant="ghost"
                >
                    <Icon
                        className="size-4"
                        icon="solar:smartphone-update-bold-duotone"
                    />
                    {t('sellerLogin.reEnroll')}
                </Button>
            </div>
        </div>
    );
};
